/**
 * Wrappers utilitaires pour animations sur cards et listes.
 *
 * Usage :
 *   <TactileCard className="...">{children}</TactileCard>
 *   <StaggeredList delay={80}>{children}</StaggeredList>
 */
import React, { useRef } from "react";

/**
 * Card avec hover lift (uniquement CSS — pas de mousemove JS pour rester fluide).
 */
export function TactileCard({ children, className = "", as: Tag = "div", ...rest }) {
  return (
    <Tag
      className={`card-tactile ${className}`}
      {...rest}
    >
      {children}
    </Tag>
  );
}

/**
 * Liste avec révélation staggered (chaque enfant apparaît avec délai).
 */
export function StaggeredList({ children, delay = 80, className = "" }) {
  const items = React.Children.toArray(children);
  return (
    <div className={className}>
      {items.map((child, i) =>
        React.isValidElement(child)
          ? React.cloneElement(child, {
              key: child.key ?? i,
              className: `${child.props.className || ""} reveal-up`,
              style: { ...(child.props.style || {}), animationDelay: `${i * delay}ms` },
            })
          : child
      )}
    </div>
  );
}

/**
 * Counter — affiche un nombre avec animation count-up à l'apparition.
 * Préfère react-spring-like via interval (léger, sans dépendance).
 */
export function CountUp({ value, duration = 900, decimals = 0, suffix = "", prefix = "", className = "" }) {
  const safeValue = Number(value) || 0;
  const reducedMotion = typeof window !== "undefined"
    && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  const [display, setDisplay] = React.useState(reducedMotion ? safeValue : 0);
  const startTime = useRef(null);
  const frame = useRef(null);
  React.useEffect(() => {
    if (reducedMotion) { setDisplay(safeValue); return; }
    startTime.current = null;
    const animate = (ts) => {
      if (!startTime.current) startTime.current = ts;
      const elapsed = ts - startTime.current;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(safeValue * eased);
      if (progress < 1) {
        frame.current = requestAnimationFrame(animate);
      } else {
        setDisplay(safeValue);
      }
    };
    frame.current = requestAnimationFrame(animate);
    return () => frame.current && cancelAnimationFrame(frame.current);
  }, [safeValue, duration, reducedMotion]);
  return (
    <span className={className}>
      {prefix}
      {display.toLocaleString("fr-FR", { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}
      {suffix}
    </span>
  );
}
